import * as React from 'react';
import { keyframes } from '@emotion/react';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import { ReactComponent as WeaverLogo } from '../assets/weaver_logo.svg';
import { useNavigate } from 'react-router-dom';
import Lottie from "react-lottie";
import lottieData from '../assets/login-lottie.json';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, loginWithGoogle } from '../redux/authReducer';
import { Link } from '@mui/joy';
import { useEffect } from 'react';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { isGoogleLoginConfigured } from '../config/appConfig';
import GoogleLoginButton from './login/GoogleLoginButton';

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const modernInputStyles = {
  '--Input-focusedThickness': '2px',
  '--Input-radius': '10px',
  '--Input-minHeight': '48px',
  '--Input-gap': '10px',
  fontSize: '0.95rem',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1.5px solid',
  borderColor: 'rgba(255,255,255,0.1)',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    borderColor: 'rgba(255,255,255,0.25)',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  '&::before': {
    display: 'none',
  },
  '&.Mui-focused': {
    borderColor: 'primary.400',
    backgroundColor: 'rgba(255,255,255,0.06)',
    boxShadow: '0 0 0 3px rgba(83, 190, 192, 0.15)',
  },
};

const Login = () => {
  const dispatch = useDispatch();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = React.useState(false);
  const [errorKey, setErrorKey] = React.useState(0);

  const googleLoginEnabled = isGoogleLoginConfigured;

  const handleGoogleLoginSuccess = React.useCallback(
    (code) => {
      dispatch(loginWithGoogle({ code }));
    },
    [dispatch]
  );

  const handleGoogleLoginError = React.useCallback(
    () => {
      dispatch({ type: 'auth/loginWithGoogle/rejected', payload: 'Google login failed' });
    },
    [dispatch]
  );

  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: lottieData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/tasks', {
        replace: true,
        state: { justLoggedIn: true },
      });
    }
  }, [isAuthenticated, navigate]);

  // Bump errorKey whenever a new error arrives to re-trigger animation
  useEffect(() => {
    if (error) {
      setErrorKey((k) => k + 1);
    }
  }, [error]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = (event) => {
    event.preventDefault();
    dispatch(loginUser(formData));
  };

  const renderLoginForm = () => (
    <Stack sx={{ gap: 4, mt: 2 }}>
      <form onSubmit={handleLogin}>
        <FormControl required>
          <FormLabel sx={{ fontWeight: 500, fontSize: '0.85rem', letterSpacing: '0.02em', mb: 0.75 }}>
            Username
          </FormLabel>
          <Input
            type="text"
            name="username"
            placeholder="Enter your username"
            value={formData.username}
            onChange={handleChange}
            startDecorator={<PersonRoundedIcon sx={{ color: 'neutral.400', fontSize: 20 }} />}
            sx={modernInputStyles}
          />
        </FormControl>
        <FormControl required>
          <FormLabel sx={{ fontWeight: 500, fontSize: '0.85rem', letterSpacing: '0.02em', mb: 0.75 }}>
            Password
          </FormLabel>
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleChange}
            startDecorator={<LockRoundedIcon sx={{ color: 'neutral.400', fontSize: 20 }} />}
            endDecorator={
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={() => setShowPassword((v) => !v)}
                sx={{
                  '--IconButton-size': '32px',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                {showPassword ? (
                  <VisibilityOffRoundedIcon sx={{ fontSize: 19, color: 'neutral.400' }} />
                ) : (
                  <VisibilityRoundedIcon sx={{ fontSize: 19, color: 'neutral.400' }} />
                )}
              </IconButton>
            }
            sx={modernInputStyles}
          />
        </FormControl>
        <Stack sx={{ gap: 3, mt: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Link level="title-sm" href="/registration" sx={{ fontSize: '0.82rem' }}>
              Don't have an account yet?
            </Link>
          </Box>
          <Button
            type="submit"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            sx={{
              '--Button-radius': '10px',
              minHeight: '48px',
              fontSize: '0.95rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
              background: 'linear-gradient(135deg, rgba(83,190,192,1) 0%, rgba(39,53,101,1) 100%)',
              transition: 'all 0.25s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(93,200,202,1) 0%, rgba(49,63,111,1) 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(83, 190, 192, 0.3)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            Sign in
          </Button>
          {googleLoginEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <Typography level="body-xs" sx={{ color: 'neutral.400', whiteSpace: 'nowrap' }}>or</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
          </Box>
          )}
          {googleLoginEnabled && (
          <GoogleLoginButton
            disabled={isLoading}
            onSuccess={handleGoogleLoginSuccess}
            onError={handleGoogleLoginError}
          />
          )}
          {error && (
            <Box
              key={errorKey}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                backgroundColor: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid rgba(220, 38, 38, 0.2)',
                animation: `${shake} 0.5s ease-in-out, ${fadeSlideIn} 0.35s ease-out`,
              }}
            >
              <ErrorOutlineRoundedIcon sx={{ color: 'danger.400', fontSize: 20 }} />
              <Typography level="body-sm" sx={{ color: 'danger.400', fontWeight: 500 }}>
                {error}
              </Typography>
            </Box>
          )}
        </Stack>
      </form>
    </Stack>
  );

  const renderLoginLeftSidePanel = () => (
    <Box
      sx={(theme) => ({
        height: '100%',
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        left: { xs: 0, md: '50vw' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        background: 'linear-gradient(225deg, rgba(39,53,101,1) 0%, rgba(83,190,192,1) 100%)',
        padding: '2rem',
      })}
    >
      <Box component="img" src="/logo_full.png" alt="logo" sx={{ width: 75 }} />
      <Typography
        level="h2"
        sx={{
          fontWeight: 'bold',
          mb: 3,
          color: '#fff',
          textAlign: 'center',
          fontFamily: "'Poppins', sans-serif",
          fontSize: '2.5em',
        }}
      >
        DCS
      </Typography>
      <Lottie options={lottieOptions} height={400} width={400} sx={{ mb: 3 }} />
      <Typography
        variant="subtitle1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          fontSize: '2em',
          fontFamily: "'Poppins', sans-serif",
          mb: 5,
        }}
      >
        Document Compliance System
      </Typography>
    </Box>
  );

  const renderMainLoginContent = () => (
    <Box
      sx={(theme) => ({
        width: { xs: '100%', md: '50vw' },
        transition: 'width var(--Transition-duration)',
        transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        backdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(255 255 255 / 0.2)',
        overflowY: 'auto',
        [theme.getColorSchemeSelector('dark')]: {
          backgroundColor: 'rgba(19 19 24 / 0.4)',
        },
      })}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', width: '100%', px: 2 }}>
        <Box component="header" sx={{ py: 2, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          <WeaverLogo width="140" height="40" />
        </Box>
        <Box
          component="main"
          sx={{
            py: 2,
            pb: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: 400,
            maxWidth: '100%',
            mx: 'auto',
            flex: 1,
            justifyContent: 'center',
            borderRadius: 'sm',
            '& form': { display: 'flex', flexDirection: 'column', gap: 2 },
            [`& .MuiFormLabel-asterisk`]: { visibility: 'hidden' },
          }}
        >
          <Stack sx={{ gap: 1, mb: 1 }}>
            <Typography component="h1" level="h3">
              Sign in
            </Typography>
          </Stack>
          {renderLoginForm()}
        </Box>
        <Box component="footer" sx={{ py: 3 }}>
          <Typography level="body-xs" sx={{ textAlign: 'center' }}>
            © Weaversoft.io {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <GlobalStyles styles={{ ':root': { '--Form-maxWidth': '800px', '--Transition-duration': '0.4s' } }} />
      {renderMainLoginContent()}
      {renderLoginLeftSidePanel()}
    </>
  );
};

export default Login;
